import React, { useState } from "react"
import { useTranslation } from "react-i18next"
import { useNavigate } from "react-router-dom"
import { Button, Card, CardBody, Table } from "reactstrap"
import { useDispatch } from "react-redux"
import { ClipboardCheck, Pencil } from "lucide-react"
import Paginations from "components/Common/Pagination"
import NoDataAvailable from "components/Shared/NoDataAvailable"
import EditAttendanceModal from "./EditAttendanceModal"
import { usePermissions } from "../../../hooks"
import InspectionAttendanceModal from "./InspectionAttendanceModal"

const headers = [
  "S. No.",
  "Station",
  "Shift",
  "Job Title",
  "Name",
  "Phone Number",
  "National ID",
  "Attendance Admin",
  "Status",
  "Check In",
  "Check Out",
  "Date",
  "Modified By",
  "Modified Date",
  // "Reason",
  "Remarks",
  "Action",
]

const AttendanceTable = ({
  onPageChange,
  data,
  currentPage,
  totalPages,
  totalCount,
  trigger,
  loading,
  perPage,
}) => {
  const navigate = useNavigate()
  const { t } = useTranslation()
  const dispatch = useDispatch()
  const [isOpen, setIsOpen] = useState(false)
  const [selectedAttendance, setSelectedAttendance] = useState({})
  const { showAll, permissions } = usePermissions("trial_run")
  const [expandedRowId, setExpandedRowId] = useState(null)
  const [isOpenInspectionAttendanceModal, setIsOpenInspectionAttendanceModal] = useState(false)

  const isCheckedBeforeToday = checkedAtStr => {
    const [date, time, meridian] = checkedAtStr.split(" ")
    const [day, month, year] = date.split("-").map(Number)
    let [hour, minute] = time.split(":").map(Number)

    if (meridian === "PM" && hour !== 12) hour += 12
    if (meridian === "AM" && hour === 12) hour = 0

    const checkedDate = new Date(year, month - 1, day, hour, minute)
    const today = new Date()
    today.setHours(0, 0, 0, 0)

    return checkedDate < today
  }

  return (
    <>
      <Card className="overflow-x-auto">
        <CardBody>
          <div className="table-responsive">
            <Table className="table mb-0">
              <thead className="table-light full_th">
                <tr>
                  {headers.map((header, index) => (
                    <th
                      key={index}
                      style={
                        header === "Job Title" ||
                        header === "Name" ||
                        header === "Check In" ||
                        header === "Check Out"
                          ? { minWidth: "165px", whiteSpace: "nowrap" }
                          : header === "Date"
                          ? { minWidth: "99px", whiteSpace: "nowrap" }
                          : undefined
                      }
                    >
                      {t(header)}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {data.map((item, index) => (
                  <tr key={item.id}>
                    <th scope="row">
                      {(currentPage - 1) * perPage + index + 1}
                    </th>
                    <td>{item?.station}</td>
                    <td>{item?.shift}</td>
                    <td>{item?.assign_job_title}</td>
                    <td>
                      <p>{item?.user?.name}</p>
                    </td>
                    <td>{item?.user?.phone}</td>
                    <td>{item?.user?.national_id}</td>
                    <td>{item?.attendance_mark_by}</td>
                    <td>
                      {item.status === 1
                        ? t("Present")
                        : item.status === 2
                        ? t("Absent")
                        : item.status === 3
                        ? t("Completed")
                        : ""}
                    </td>
                    <td>{item?.checkin_at || "-"}</td>
                    <td>{item?.checkout_at || "-"}</td>
                    <td>{item?.checked_at?.split(" ")[0]}</td>
                    <td>{item?.last_attendance_edit_by || "-"}</td>
                    <td>{item?.last_edit_at || "-"}</td>
                    {/*<td>{item?.reason}</td>*/}
                    <td
                      onClick={() =>
                        setExpandedRowId(expandedRowId === item.id ? null : item.id)
                      }
                      style={{
                        maxWidth: "150px",
                        cursor: "pointer",
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        whiteSpace: expandedRowId === item.id ? "normal" : "nowrap",
                        wordBreak: "break-word"
                      }}
                      title="Click to expand"
                    >
                      {item.remarks}
                    </td>
                    <td>
                      <div className="d-flex gap-2 action_buttons">
                        {(showAll ||
                          (isCheckedBeforeToday(item.checked_at) &&
                            permissions.includes("can_edit_attendance")) ||
                          permissions.includes(
                            "can_edit_attendance_with_back_date"
                          )) && (
                          <Button
                            color="secondary"
                            onClick={() => {
                              setIsOpen(!isOpen)
                              setSelectedAttendance(item)
                            }}
                            //   disabled={
                            //     isLoading && item?.uuid === itemIdToDelete
                            //   }
                            title={t("Edit Attendance")}
                          >
                            <Pencil />
                          </Button>
                        )}
                        {(item.status === 1) && (showAll || permissions.includes("can_capture_random_check"))
                          && (
                          <Button
                            color="primary"
                            onClick={() => {
                              setIsOpenInspectionAttendanceModal(!isOpenInspectionAttendanceModal)
                              setSelectedAttendance(item)
                            }}
                            //   disabled={
                            //     isLoading && item?.uuid === itemIdToDelete
                            //   }
                            title={t("Random Check In")}
                          >
                            <ClipboardCheck/>
                          </Button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </Table>
          </div>
          {!loading && data.length === 0 && <NoDataAvailable />}
        </CardBody>
      </Card>
      {data.length !== 0 && (
        <Paginations
          currentPage={currentPage}
          totalPages={totalPages}
          totalCount={totalCount}
          onPageChange={onPageChange}
        />
      )}
      {isOpen && (
        <EditAttendanceModal
          isOpen={isOpen}
          toggle={() => {
            setIsOpen(false)
            trigger(!trigger)
          }}
          attendanceDetails={selectedAttendance}
        />
      )}
      {isOpenInspectionAttendanceModal && (
        <InspectionAttendanceModal
          isOpen={isOpenInspectionAttendanceModal}
          toggle={() => {
            setIsOpenInspectionAttendanceModal(false)
            trigger(!trigger)
          }}
          attendanceDetails={selectedAttendance}
        />
      )}
    </>
  )
}

export default AttendanceTable
